/**
 * U2.4. The taste questions — the second step of onboarding
 * (`screens/Onboarding.tsx`) and "Your taste" under Account. One component,
 * as `ProfileForm` is, so the two cannot drift.
 *
 * Controlled: the caller owns the `TasteDraft` (`lib/tasteForm.ts`) and decides
 * when it is written. Every decision — what a draft becomes, how names split,
 * which venue a name is — is in `tasteForm.ts`; this file draws controls.
 *
 * The four-step control is the app's `Segmented` (the same track as Theme and
 * the list kinds), spread across the row with `u-seg--fit`; no button style
 * was added. A lane is a row with its name above the control, because at 390px
 * a name and four segments do not share a line.
 */
import { useId, type ReactNode } from 'react'
import { Field, Segmented } from '../ui'
import { currentRegion, useRegionsNow } from '../lib/regions'
import { Group, Toggle } from './ProfileForm'
import {
  LANE_ROWS, LEVEL_OPTIONS, asksClassicalFlags, freeTextMax, levelOf, type Level, type TasteDraft,
} from '../lib/tasteForm'
import './ProfileForm.css'
import './TasteForm.css'

/** What the names box suggests (C2.3): the kind of thing it wants, in the
 *  region being looked at. A hint, not data. */
const BERLIN_NAMES = 'Berghain, Kronos Quartet, Berlinale'
const NAMES_PLACEHOLDER = new Map<string, string>([
  ['berlin', BERLIN_NAMES],
  ['boston', 'Symphony Hall, Kronos Quartet, IFFBoston'],
])

export function TasteForm({ draft, onChange, englishOnly }: {
  draft: TasteDraft
  onChange: (next: TasteDraft) => void
  /** `profiles.english_only`: it decides how much room the person's own words
   *  have (`freeTextMax`), and is not asked again here. */
  englishOnly: boolean
}) {
  const set = <K extends keyof TasteDraft>(key: K, value: TasteDraft[K]) => onChange({ ...draft, [key]: value })
  const max = freeTextMax(englishOnly)
  const namesPlaceholder = NAMES_PLACEHOLDER.get(currentRegion(useRegionsNow()).id) ?? BERLIN_NAMES
  return (
    <div className="pf tf">
      <Group title="How much of each?" hint="Never hides it. Always puts it first.">
        <div className="tf__lanes">
          {LANE_ROWS.map(({ lane, label }) => (
            <LaneRow key={lane} label={label} value={draft.lanes[lane]}
                     onChange={level => onChange({ ...draft, lanes: { ...draft.lanes, [lane]: level } })}>
              {lane === 'classical' && asksClassicalFlags(draft) && (
                <div className="tf__flags">
                  <Toggle label="Only orchestral" meta="Leaves out everything that is not an orchestra."
                          on={draft.onlyOrchestral} onChange={v => set('onlyOrchestral', v)} />
                  {!draft.onlyOrchestral && (
                    <>
                      <Toggle label="Chamber music too" meta="Quartets, recitals, song and organ."
                              on={draft.chamberToo} onChange={v => set('chamberToo', v)} />
                      <Toggle label="Film music too" meta="Concerts of film scores."
                              on={draft.filmMusicToo} onChange={v => set('filmMusicToo', v)} />
                    </>
                  )}
                </div>
              )}
            </LaneRow>
          ))}
        </div>
      </Group>

      <Field label="A few names" hint="Artists, composers, venues, festivals you'd never miss. Separate them with commas.">
        <Field.Textarea rows={3} value={draft.names} autoCapitalize="words" autoCorrect="off"
                        placeholder={namesPlaceholder}
                        onChange={e => set('names', e.currentTarget.value)} />
      </Field>

      <Field label="Anything else?" hint={`${draft.more.length} of ${max}. Kept as you wrote it.`}>
        <Field.Textarea rows={4} value={draft.more} maxLength={max}
                        onChange={e => set('more', e.currentTarget.value)} />
      </Field>
    </div>
  )
}

function LaneRow({ label, value, onChange, children }: {
  label: string
  value: Level
  onChange: (level: Level) => void
  children?: ReactNode
}) {
  const id = useId()
  return (
    <div className="tf__lane" role="group" aria-labelledby={id}>
      <span className="tf__name" id={id}>{label}</span>
      <Segmented className="u-seg--fit" aria-labelledby={id}
                 value={String(value)} onChange={v => onChange(levelOf(v))}
                 options={LEVEL_OPTIONS.map(o => ({ ...o }))} />
      {children}
    </div>
  )
}
